import { useState, useEffect, ReactNode } from 'react';
import axios, { AxiosError } from 'axios';
import { ActiveContextContext } from './activeContextDefinition';

export const ActiveContextProvider = ({ children }: { children: ReactNode }) => {
  const [activeContextId, setActiveContextIdState] = useState<string | null>(() => {
    // Try to restore from sessionStorage (per-tab storage)
    const storedContextId = sessionStorage.getItem('activeContextId');

    // IMPORTANT: Set axios header synchronously during initialization
    // This prevents race condition where queries execute before useEffect runs
    if (storedContextId) {
      axios.defaults.headers.common['X-Active-Context-Id'] = storedContextId;
    }

    return storedContextId;
  });

  // Add global axios interceptor to clear invalid context IDs
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // If we get a 403 with "requested context" error, the stored context ID is invalid
        if (error.response?.status === 403) {
          const data = error.response.data as { description?: string };
          if (data.description?.includes('requested context')) {
            console.warn('Stored context ID is invalid, clearing...');
            sessionStorage.removeItem('activeContextId');
            delete axios.defaults.headers.common['X-Active-Context-Id'];
            setActiveContextIdState(null);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Update sessionStorage and axios header whenever activeContextId changes
  useEffect(() => {
    if (activeContextId) {
      sessionStorage.setItem('activeContextId', activeContextId);

      // Configure axios to send the header with all requests
      axios.defaults.headers.common['X-Active-Context-Id'] = activeContextId;
    } else {
      sessionStorage.removeItem('activeContextId');
      delete axios.defaults.headers.common['X-Active-Context-Id'];
    }
  }, [activeContextId]);

  const setActiveContextId = (contextId: string | null) => {
    // IMPORTANT: Set axios header and sessionStorage synchronously BEFORE updating state
    // This ensures the header is set before any re-renders and query refetches happen
    if (contextId) {
      sessionStorage.setItem('activeContextId', contextId);
      axios.defaults.headers.common['X-Active-Context-Id'] = contextId;
    } else {
      sessionStorage.removeItem('activeContextId');
      delete axios.defaults.headers.common['X-Active-Context-Id'];
    }

    // Now update state, which will trigger re-render
    setActiveContextIdState(contextId);
  };

  return (
    <ActiveContextContext.Provider value={{ activeContextId, setActiveContextId }}>
      {children}
    </ActiveContextContext.Provider>
  );
};
